import { api } from './api.ts'

/**
 * Ponte con l'app CIDIS per iPhone e Android (484, docs/13-app-mobile.md).
 *
 * L'app degli store e' un guscio Capacitor che carica questa stessa interfaccia dall'indirizzo del
 * gestionale. Dentro l'app esiste l'oggetto globale `window.Capacitor`, con i plugin nativi in
 * `Capacitor.Plugins`; nel browser non esiste, e qui tutto diventa un'operazione vuota.
 *
 * Il bundle non importa pacchetti @capacitor di proposito: lo stesso codice gira nel browser di
 * tutti, e il guscio porta gia' con se' il ponte nativo. Basta un'interfaccia minima, tipizzata qui,
 * dei pochi metodi che servono.
 *
 * Il percorso: all'apertura dell'app si ascoltano i tocchi sulle notifiche (anche prima
 * dell'accesso, perche' il tocco che ha aperto l'app si consegna appena qualcuno ascolta); dopo
 * l'accesso si chiede il permesso, la prima volta, e il token del telefono va a POST /dispositivi;
 * all'uscita il telefono si toglie con DELETE /dispositivi. Il tocco porta sempre alla pagina degli
 * avvisi: la push non dice di che avviso si tratta (apps/api/src/servizi/push.ts).
 */

const PERCORSO_AVVISI = '/notifiche'

type StatoPermesso = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
type Ascolto = { remove: () => Promise<void> }
type NotificaNativa = { data?: Record<string, unknown> }

/** I metodi di @capacitor/push-notifications che si usano. */
type PluginPush = {
  checkPermissions(): Promise<{ receive: StatoPermesso }>
  requestPermissions(): Promise<{ receive: StatoPermesso }>
  register(): Promise<void>
  addListener(evento: 'registration', f: (token: { value: string }) => void): Promise<Ascolto>
  addListener(evento: 'registrationError', f: (errore: { error: string }) => void): Promise<Ascolto>
  addListener(evento: 'pushNotificationReceived', f: (notifica: NotificaNativa) => void): Promise<Ascolto>
  addListener(evento: 'pushNotificationActionPerformed',
    f: (azione: { actionId: string; notification: NotificaNativa }) => void): Promise<Ascolto>
  removeAllDeliveredNotifications?(): Promise<void>
}

/** @capacitor/device, se il guscio lo porta: serve solo a proporre un nome al telefono. */
type PluginDispositivo = { getInfo(): Promise<{ name?: string; model?: string }> }

type CapacitorGlobale = {
  getPlatform(): string
  isNativePlatform?(): boolean
  Plugins?: Record<string, unknown>
  registerPlugin?<T>(nome: string): T
}

type Piattaforma = 'ios' | 'android'

function capacitor(): CapacitorGlobale | null {
  const c = (window as unknown as { Capacitor?: CapacitorGlobale }).Capacitor
  if (!c || typeof c.getPlatform !== 'function') return null
  return (c.isNativePlatform ? c.isNativePlatform() : c.getPlatform() !== 'web') ? c : null
}

function piattaforma(): Piattaforma | null {
  const p = capacitor()?.getPlatform()
  return p === 'ios' || p === 'android' ? p : null
}

/** Il plugin dal ponte nativo; se il ponte non l'ha ancora esposto, lo si chiede per nome. */
function plugin<T>(nome: string): T | null {
  const c = capacitor()
  if (!c) return null
  const esposto = c.Plugins?.[nome] as T | undefined
  if (esposto) return esposto
  try {
    return c.registerPlugin ? c.registerPlugin<T>(nome) : null
  } catch {
    return null
  }
}

/** Vero dentro l'app per iPhone o Android, falso nel browser. */
export function inAppNativa(): boolean {
  return piattaforma() !== null
}

// --- quale telefono --------------------------------------------------------------------------------
//
// Per gli inviti e le istruzioni (AvvisoAppTelefono, AppTelefono, IstruzioniElement): decidono che
// cosa mostrare, non che cosa si puo' fare.

/**
 * Dentro l'app CIDIS: il ponte nativo, oppure l'user agent che il guscio aggiunge (`appendUserAgent`
 * in mobile/capacitor.config.ts). Il secondo regge anche se il ponte non si e' ancora presentato.
 */
export function nellAppCidis(): boolean {
  return inAppNativa() || /CIDIS-App/.test(navigator.userAgent)
}

/** Il sistema del telefono dall'user agent; 'altro' e' il computer. */
export function sistemaTelefono(): 'iphone' | 'android' | 'altro' {
  const ua = navigator.userAgent
  // L'iPad con iPadOS si presenta come un Mac: lo tradisce lo schermo tattile.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iphone'
  if (/Android/.test(ua)) return 'android'
  return 'altro'
}

/** Il gestionale aperto dall'icona della schermata Home (manifest.webmanifest), non da una scheda del browser. */
export function daSchermataHome(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

// --- stato -----------------------------------------------------------------------------------------

/** Token del telefono, come l'ha consegnato il sistema. Resta in memoria, mai nel browser. */
let token: string | null = null
/** C'e' un utente entrato: solo allora il token va al gestionale. */
let dentro = false
/** Ultimo token consegnato in questa sessione: lo stesso evento ripetuto non rifa' la richiesta. */
let consegnato: string | null = null
/** Identificativo del telefono nel gestionale, per riconoscerlo fra i «Telefoni collegati». */
let idDispositivo: string | null = null
let preparato = false
let azioni: { vai: (percorso: string) => void; nuovaNotifica: () => void } | null = null

/** Un percorso interno del gestionale, o la pagina degli avvisi: un dato arrivato da fuori non porta altrove. */
function percorsoSicuro(valore: unknown): string {
  return typeof valore === 'string' && /^\/[A-Za-z0-9/_-]*$/.test(valore) && !valore.startsWith('//')
    ? valore : PERCORSO_AVVISI
}

async function nomeDelTelefono(): Promise<string | null> {
  try {
    const info = await plugin<PluginDispositivo>('Device')?.getInfo()
    const nome = (info?.name || info?.model || '').trim()
    return nome ? nome.slice(0, 100) : null
  } catch {
    return null
  }
}

async function consegnaToken(): Promise<void> {
  const p = piattaforma()
  if (!dentro || !token || !p || consegnato === token) return
  consegnato = token
  try {
    const r = await api.post<{ id: string }>('/dispositivi', { piattaforma: p, token, nome: await nomeDelTelefono() })
    idDispositivo = r.id
  } catch {
    // Senza registrazione il telefono non riceve push, e basta: l'app funziona lo stesso. Si
    // riprova alla prossima apertura.
    consegnato = null
  }
}

// --- comandi ---------------------------------------------------------------------------------------

/**
 * All'apertura dell'app, una volta sola: ascolta il token e i tocchi sulle notifiche. `vai` porta a
 * una pagina, `nuovaNotifica` aggiorna il contatore quando una push arriva con l'app in primo piano.
 * Le funzioni si aggiornano a ogni chiamata, gli ascolti no.
 */
export function preparaAppNativa(opzioni: { vai: (percorso: string) => void; nuovaNotifica: () => void }): void {
  azioni = opzioni
  if (preparato) return
  const push = plugin<PluginPush>('PushNotifications')
  if (!push || !piattaforma()) return
  preparato = true
  const ignora = () => undefined
  ascolta(() => push.addListener('registration', (t) => { token = t.value; void consegnaToken() }))
  // Un errore di registrazione (niente Google Play, permesso ritirato) lascia il telefono senza
  // push: non c'e' altro da fare, e non e' un motivo per disturbare chi lavora.
  ascolta(() => push.addListener('registrationError', ignora))
  ascolta(() => push.addListener('pushNotificationReceived', () => azioni?.nuovaNotifica()))
  ascolta(() => push.addListener('pushNotificationActionPerformed',
    (a) => azioni?.vai(percorsoSicuro(a.notification?.data?.percorso))))
}

/**
 * Un ascolto sul ponte nativo, senza che un suo errore arrivi alla pagina. Il ponte di Capacitor 8
 * restituisce da `addListener` un valore e non una promessa (30/10/2026: `.catch` sul risultato
 * mandava in errore l'avvio e l'app restava bianca), quindi il risultato si avvolge sempre.
 */
function ascolta(f: () => unknown): void {
  try {
    void Promise.resolve(f()).catch(() => undefined)
  } catch { /* plugin senza l'evento: l'app resta senza push */ }
}

/**
 * Dopo l'accesso: chiede il permesso la prima volta e registra il telefono. Se il permesso e' stato
 * negato non lo richiede: lo si riaccende dalle impostazioni del telefono.
 */
export async function attivaNotifichePush(): Promise<void> {
  const push = plugin<PluginPush>('PushNotifications')
  if (!push || !piattaforma()) return
  dentro = true
  try {
    let permesso = await push.checkPermissions()
    if (permesso.receive === 'prompt' || permesso.receive === 'prompt-with-rationale') {
      permesso = await push.requestPermissions()
    }
    if (permesso.receive !== 'granted') return
    // Il token arriva con l'evento `registration`; se c'era gia', si consegna subito.
    await push.register()
    await consegnaToken()
  } catch {
    // Plugin assente o rifiuto del sistema: l'app resta senza push.
  }
}

/**
 * All'uscita: il telefono si toglie dai «Telefoni collegati» e dallo schermo spariscono le notifiche
 * gia' arrivate. Va chiamata prima di chiudere la sessione, che serve alla richiesta. Non lancia:
 * l'uscita non si ferma per questo.
 */
export async function scollegaTelefono(): Promise<void> {
  const p = piattaforma()
  const t = token
  dentro = false
  consegnato = null
  idDispositivo = null
  if (!p || !t) return
  try {
    await api.elimina('/dispositivi', { piattaforma: p, token: t })
  } catch { /* la sessione puo' essere gia' scaduta: il telefono si toglie da «Telefoni collegati» */ }
  await togliNotificheConsegnate()
}

/** Toglie dallo schermo del telefono le notifiche gia' arrivate: aperta la pagina degli avvisi non servono. */
export async function togliNotificheConsegnate(): Promise<void> {
  try {
    await plugin<PluginPush>('PushNotifications')?.removeAllDeliveredNotifications?.()
  } catch { /* niente da togliere, o plugin senza il metodo */ }
}

/** Il telefono su cui gira l'app, se e' registrato: «questo telefono» nell'elenco. */
export function telefonoCorrente(): string | null {
  return idDispositivo
}
